import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import Paper from '@material-ui/core/Paper';
import { useTranslation } from 'react-i18next';

import PinDropIcon from '@material-ui/icons/PinDrop';
import EmailIcon from '@material-ui/icons/Email';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import { BoothContext } from 'pages/booths/BoothProfileContainer';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import useJoinGroupChatSwitch from 'hooks/useJoinGroupChatSwitch';
import useEncodedMeetupUrl from 'hooks/useEncodedMeetupUrl';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  border: 1px solid transparent;
  border-radius: 20px;
`;

const TitleContainer = styled.div`
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 24px;
  padding-top: 8px;
  padding-bottom: 12px;
  background-color: #EFEFEF;
`;

const BtnGroupContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex-wrap: wrap;
  background-color: #CECECE;
  padding: 8px 0;
  font-size: 10px;
  z-index: 1;
`;

const StyledHyperLink = styled.a`
  text-decoration: none;
  color: #000;
`;

const BtnContainer = styled.div`
  cursor: pointer;
  display: flex;
  border: 1px solid #ABABAB;
  border-radius: 6px;
  padding: 0 2px;
  font-weight: 600;
  background-color: #EFEFEF;
  line-height: 26px;
`;

interface BusinessProps {
  boothId: string;
  boothName: string;
  emailContacts: any;
  meetupUrl: string;
}

const BusinessPopover: React.FC<BusinessProps> = ({
  boothId,
  boothName,
  emailContacts,
  meetupUrl,
}: BusinessProps) => {
  const { t } = useTranslation();
  const { hall, chatroomConfig } = useSelector((state) => state.eventConfigs);
  const locale = useSelector(selectLocale);
  const encodedMeetupUrl = useEncodedMeetupUrl(meetupUrl);
  const { joinBoothChat } = useJoinGroupChatSwitch();
  const boothForMatomo = useContext(BoothContext);
  const mailToLink = useMemo(() => {
    if (emailContacts && emailContacts.length > 0) {
      const email = emailContacts.find((eachemailContact) => (eachemailContact.type === 'email'));
      if (email?.contact) {
        return `mailto:${email.contact}`;
      }
    }
    return '';
  }, [emailContacts]);

  const openChatWindow = () => {
    window.trackingItemToFirebase(null, 'Booth', 'Chat', 'Group_Chat_Click', 'Booth_Profile', boothId, boothForMatomo.boothNameForMatomo);
    joinBoothChat(boothId, boothName);
  };

  const clickMeetup = () => {
    window.trackingItemToFirebase(null, 'Booth', 'Meetup', 'Meetup_Click', 'Booth_Profile', boothId, boothForMatomo.boothNameForMatomo);
  };

  const clickLeaveMessage = () => {
    window.trackingItemToFirebase(null, 'Booth', 'Leave a Message', 'Leave a Message_Click', 'Booth_Profile', boothId, boothForMatomo.boothNameForMatomo);
  };

  const boothGreeting = useMemo(() => findAvailableLocale(hall?.popOver?.boothGreeting, locale) || t('booth.greeting', 'Hi, feel free to talk to us!'), [t, locale, hall]);
  const chatMessage = useMemo(() => findAvailableLocale(hall?.popOver?.chat, locale) || t('booth.chat', 'Chat'), [t, locale, hall]);
  const mailToMessage = useMemo(() => findAvailableLocale(hall?.popOver?.mailTo, locale) || t('booth.message', 'Message'), [t, locale, hall]);
  const meetupMessage = useMemo(() => findAvailableLocale(hall?.popOver?.meetup, locale) || t('booth.meetup', 'Meetup'), [t, locale, hall]);

  return (
    <Paper elevation={3}>
      <Container>
        <TitleContainer>
          {boothGreeting}
        </TitleContainer>
        <BtnGroupContainer>
          {!isChatroomDisabled(chatroomConfig) && (
            <BtnContainer onClick={openChatWindow}>
              <QuestionAnswerIcon />
              <div>
                {chatMessage}
              </div>
            </BtnContainer>
          )}
          {
            mailToLink && (
              <StyledHyperLink href={mailToLink} target="_blank" rel="noopener noreferrer">
                <BtnContainer onClick={clickLeaveMessage}>
                  <EmailIcon />
                  <div>{mailToMessage}</div>
                </BtnContainer>
              </StyledHyperLink>
            )
          }
          {
            encodedMeetupUrl && (
              <StyledHyperLink href={encodedMeetupUrl} target="_blank" rel="noopener noreferrer">
                <BtnContainer onClick={clickMeetup}>
                  <PinDropIcon />
                  <div>{meetupMessage}</div>
                </BtnContainer>
              </StyledHyperLink>
            )
          }
        </BtnGroupContainer>
      </Container>
    </Paper>
  );
};

export default BusinessPopover;
